import { gql, useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { useCallback } from 'react';

import { Provider } from '../types';

export type GetCurrentProviderQueryData = {
  currentProvider: Required<Provider>;
};

export const GET_CURRENT_PROVIDER_GQL = gql`
  query GetCurrentProvider {
    currentProvider {
      shortId
      name
      image
      pronouns
      isPrescriber
    }
  }
`;

export function useGetCurrentProviderQuery() {
  return useQuery<GetCurrentProviderQueryData>(GET_CURRENT_PROVIDER_GQL);
}

export function useGetCurrentProviderQueryCallback() {
  return useLazyQuery<GetCurrentProviderQueryData>(GET_CURRENT_PROVIDER_GQL);
}

/**
 * Custom hook to lazily fetch the current provider as well as refetch all
 * active queries on successful response.
 *
 * @returns A tuple containing an Apollo client function to fetch the current
 * provider and the result of the lazy query.
 */
export function useGetCurrentProviderQueryCallbackWithRefetch() {
  const client = useApolloClient();
  const [fetch, { ...result }] = useGetCurrentProviderQueryCallback();

  const fetchWithRefetch = useCallback(
    async (...args: Parameters<typeof fetch>) => {
      const response = await fetch(...args);
      if (response.data?.currentProvider) {
        await client.refetchQueries({ include: 'active' });
      }
      return response;
    },
    [fetch, client],
  );
  return [fetchWithRefetch, result] as const;
}
